import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { LANGUAGES } from '../lib/translations';
import { Car, Menu, X, ChevronDown, Globe, User, LogOut, LayoutDashboard, PlusCircle, MessageSquare, Lock, Shield, Download } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, changeLang } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0F19]/80 border-b border-[#1E293B]" data-testid="main-header">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Security */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <img
                src="/euromoto_icon_512.png"
                alt="EuroMoto Logo"
                className="w-9 h-9 rounded-lg object-cover transition-transform group-hover:scale-105"
                data-testid="header-logo-image"
              />
              <span className="font-bold text-xl text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Euro<span className="text-[#FADB5F]">Moto</span><span className="text-[#FADB5F]/60 text-sm">.Online</span>
              </span>
            </Link>
            <div className="hidden sm:flex items-center gap-2 ml-2">
              <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-[#1E293B]/60 border border-[#1E293B]">
                <Lock className="w-2.5 h-2.5 text-green-400" />
                <span className="text-[9px] text-[#94A3B8] font-medium">SSL</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-[#1E293B]/60 border border-[#1E293B]">
                <Shield className="w-2.5 h-2.5 text-[#FADB5F]" />
                <span className="text-[9px] text-[#94A3B8] font-medium">GDPR</span>
              </div>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/" className="px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F] transition-colors" data-testid="nav-home">
              {t('nav.home')}
            </Link>
            <Link to="/listings" className="px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F] transition-colors" data-testid="nav-listings">
              {t('nav.listings')}
            </Link>
            {user && (
              <>
                <Link to="/create-listing" className="px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F] transition-colors" data-testid="nav-create">
                  {t('nav.create')}
                </Link>
                <Link to="/dashboard" className="px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F] transition-colors" data-testid="nav-dashboard">
                  {t('nav.dashboard')}
                </Link>
              </>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Android Download Button */}
            <a
              href="/EuroMoto.apk"
              download="EuroMoto.apk"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#FADB5F]/10 border border-[#FADB5F]/20 text-[#FADB5F] hover:bg-[#FADB5F]/20 transition-colors"
              data-testid="header-download-apk"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="text-[10px] font-semibold">APK</span>
            </a>
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-[#94A3B8] hover:text-[#FADB5F]" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs font-medium">{currentLang?.name}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-h-80">
                <ScrollArea className="h-72">
                  {LANGUAGES.map(l => (
                    <DropdownMenuItem
                      key={l.code}
                      onClick={() => changeLang(l.code)}
                      className={`cursor-pointer ${lang === l.code ? 'bg-[#0A3A82]/5 text-[#0A3A82] font-medium' : ''}`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span className="mr-2 text-xs font-mono">{l.flag}</span>
                      {l.name}
                    </DropdownMenuItem>
                  ))}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5 text-[#94A3B8] hover:text-[#FADB5F]" data-testid="user-menu-trigger">
                    <div className="w-7 h-7 rounded-full bg-[#0A3A82] flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-[#F1F5F9]">{user.name}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer" data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/create-listing')} className="cursor-pointer" data-testid="menu-create">
                    <PlusCircle className="w-4 h-4 mr-2" /> {t('nav.create')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=messages')} className="cursor-pointer" data-testid="menu-messages">
                    <MessageSquare className="w-4 h-4 mr-2" /> {t('nav.messages')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-[#FADB5F] font-medium">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button size="sm" className="bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-medium">
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-[#94A3B8]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#1E293B] bg-[#0B0F19]">
          <div className="px-4 py-3 space-y-2">
            <Link to="/listings" className="block px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-listings">
              {t('nav.listings')}
            </Link>
            {user ? (
              <>
                <Link to="/create-listing" className="block px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-create">
                  {t('nav.create')}
                </Link>
                <Link to="/dashboard" className="block px-3 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#FADB5F]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-dashboard">
                  {t('nav.dashboard')}
                </Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block w-full text-left px-3 py-2 text-sm font-medium text-red-400" data-testid="mobile-nav-logout">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block px-3 py-2 text-sm font-medium text-[#FADB5F]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-login">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="block px-3 py-2 text-sm font-medium text-[#FADB5F]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-register">
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
